import { api } from '../lib/api';
import type {
  BrandingSettings,
  BrandLogos,
  BrandTheme,
  DomainStage,
  LogoShape,
  LogoTheme,
} from '../lib/types';

/**
 * The team's logos, color, slates and their own domain.
 *
 * Every call here answers with the whole settings object, so the page never
 * has to merge a partial response into what it was already showing.
 */

export function fetchBranding(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding`);
}

export interface BrandingUpdate {
  /** `null` clears the color; omitted leaves it alone. */
  brandColor?: string | null;
  introVideoId?: string | null;
  outroVideoId?: string | null;
  theme?: BrandTheme;
  /** Take the credit line and the signup link off their share pages. */
  hideSlateBranding?: boolean;
}

export function updateBranding(orgId: string, body: BrandingUpdate): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding`, { method: 'PUT', body });
}

/**
 * Which of the four slots: a shape, and the lighting the mark was drawn for.
 */
export interface LogoSlot {
  shape: LogoShape;
  theme: LogoTheme;
}

/** The slot as one string, for keying a mutation on which one is busy. */
export function slotKey(slot: LogoSlot): string {
  return `${slot.shape}:${slot.theme}`;
}

/**
 * The logo to draw on a surface lit `theme`, or null when the team has none.
 *
 * Legibility before shape: the right set in the shape asked for, then the
 * right set in the other shape, then the other set. A mark drawn for the
 * other lighting is often invisible (a white wordmark on a white page), so a
 * square logo that can be seen beats a wide one that cannot. A team with one
 * set uploaded gets it everywhere, as they did before there were two.
 *
 * Kept identical to `logoFor` in slate-api/src/policy/branding.ts, which is
 * the authority and applies the same rule to emails and link previews.
 */
export function logoFor(
  logos: BrandLogos | null | undefined,
  theme: LogoTheme,
  shape: LogoShape,
): string | null {
  if (!logos) return null;
  const other: LogoTheme = theme === 'dark' ? 'light' : 'dark';
  const otherShape: LogoShape = shape === 'wide' ? 'square' : 'wide';
  return logos[theme][shape] ?? logos[theme][otherShape] ?? logos[other][shape] ?? logos[other][otherShape];
}

/** True when any slot is filled: the team has a logo at all. */
export function hasAnyLogo(logos: BrandLogos | null | undefined): boolean {
  if (!logos) return false;
  return Boolean(logos.dark.square || logos.dark.wide || logos.light.square || logos.light.wide);
}

/**
 * A logo, sent as base64 in JSON like a profile picture. The file is read
 * here rather than in the component so every caller gets the same size and
 * type guard.
 */
export async function uploadLogo(orgId: string, slot: LogoSlot, file: File): Promise<BrandingSettings> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      // "data:image/png;base64,AAAA" — the API wants only what follows.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/logo`, {
    method: 'PUT',
    body: { kind: slot.shape, theme: slot.theme, contentType: file.type, data },
  });
}

export function removeLogo(orgId: string, slot: LogoSlot): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/logo?kind=${slot.shape}&theme=${slot.theme}`, {
    method: 'DELETE',
  });
}

/** Claim a hostname and start the walk towards serving links from it. */
export function setCustomDomain(orgId: string, host: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain`, { method: 'PUT', body: { host } });
}

/** "Check again": asks AWS and DNS right now rather than on the next poll. */
export function checkCustomDomain(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain/verify`, { method: 'POST' });
}

export function removeCustomDomain(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain`, { method: 'DELETE' });
}

/** What the images are allowed to be, mirroring the API's own list. */
// PNG and JPEG only. An SVG is a document that can carry script, and a logo
// is served from the media domain to every viewer of the team's pages.
export const LOGO_TYPES = ['image/png', 'image/jpeg'];
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Black or white, whichever can be read on this color.
 *
 * The API computes the same thing and stores it with the color; this copy
 * exists for the preview, which follows what is being typed rather than what
 * has been saved. Kept deliberately identical to `readableInkOn` in
 * slate-api/src/policy/branding.ts, which is the authority.
 */
export function inkFor(value: string): string {
  const hex = value.replace('#', '').trim();
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (full.length !== 6) return '#ffffff';
  const channel = (pair: string): number => {
    const srgb = Number.parseInt(pair, 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(full.slice(0, 2)) + 0.7152 * channel(full.slice(2, 4)) + 0.0722 * channel(full.slice(4, 6));
  return luminance > 0.45 ? '#000000' : '#ffffff';
}

/** Why this file will not do, or null. Checked here so the API is not asked. */
export function logoProblem(file: File): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'Use a PNG or JPEG image.';
  if (file.size > LOGO_MAX_BYTES) return 'Logos are limited to 2 MB.';
  return null;
}

/**
 * What the domain is doing, said to the person waiting on it, and always from
 * their point of view: whether the next move is theirs or ours.
 */
export function domainLabel(stage: DomainStage): { title: string; tone: 'info' | 'caution' | 'go' | 'danger' } {
  switch (stage) {
    case 'requesting':
      return { title: 'Preparing your DNS record', tone: 'info' };
    case 'dns':
      return { title: 'Waiting for your DNS record', tone: 'caution' };
    case 'building':
      return { title: 'Building the certificate and CDN', tone: 'info' };
    case 'live':
      return { title: 'Serving your links', tone: 'go' };
    case 'failed':
      return { title: 'Could not be set up', tone: 'danger' };
    default:
      return { title: 'No domain yet', tone: 'info' };
  }
}

/** Is this one the settings page should keep asking about? */
export function domainInFlight(stage: DomainStage): boolean {
  return stage === 'requesting' || stage === 'dns' || stage === 'building';
}

/**
 * How often to ask again. `requesting` is ours and usually resolves in a
 * second or two, so it is worth watching closely; the others wait on a DNS
 * change or on AWS, neither of which rewards impatience.
 */
export function domainPollMs(stage: DomainStage): number {
  return stage === 'requesting' ? 3_000 : 15_000;
}
